/**
 * SSE 读流 —— 契约 §5.2。
 *
 * ⚠ 不能用浏览器原生 `EventSource`：令牌走 `Authorization` 头，而它设不了头。
 *   这条是硬限制(已写成 lint 错误)，所以这里用 `fetch` + `ReadableStream` 自己读。
 */
import { createParser } from 'eventsource-parser'
import { decodeSseEvent, type AgentEvent, type SseDecodeFailure } from '@cione/contracts'
import { ApiError, CLIENT_ABORTED, CLIENT_NETWORK } from './errors'
import { TRACE_ID_HEADER, type HttpClient } from './http'

/** 流为什么结束。⚠ 三种要分开：界面对它们的处置完全不同。 */
export type StreamEndReason =
  /** 收到了 `done` —— 服务端正常收尾 */
  | 'done'
  /** 连接断了但没收到 `done` —— 网络断，界面要能重连或提示 */
  | 'closed'
  /** 用户主动取消 */
  | 'aborted'

export interface ReadAgentStreamOptions {
  http: HttpClient
  /** run 返回的 `streamChannel`，形如 `agent/9f2c...`。 */
  streamChannel: string
  onEvent: (event: AgentEvent) => void
  /** ⚠ 坏数据只记一条就跳过，绝不中断整条流。 */
  onDecodeFailure?: ((failure: SseDecodeFailure) => void) | undefined
  signal?: AbortSignal | undefined
  /** 401 时刷新令牌并重开一次。不给就直接抛。 */
  onUnauthorized?:
    ((error: ApiError, accessTokenUsed: string | null) => Promise<boolean>) | undefined
}

export async function readAgentStream(options: ReadAgentStreamOptions): Promise<StreamEndReason> {
  const opened = await openStream(options, true)
  return consume(opened, options)
}

async function openStream(options: ReadAgentStreamOptions, mayRetry: boolean): Promise<Response> {
  const { http, streamChannel } = options
  // ⚠⚠ streamChannel 含斜杠(`agent/<taskId>`)，网关与 Python 侧都按 {path} 匹配。
  //    **绝不能 encodeURIComponent** —— 斜杠变成 %2F 后查不到这条 channel。
  const url = `${http.baseUrl}/api/agent/stream/${streamChannel}`
  const tokenUsed = http.tokenStore.get()?.accessToken ?? null

  let response: Response
  try {
    response = await http.fetchImpl(url, {
      method: 'GET',
      // ⚠ 这里**不设超时**：SSE 是长连接，套上 HTTP 内核那个 15 秒会把正常的流掐断
      ...(options.signal ? { signal: options.signal } : {}),
      headers: { Accept: 'text/event-stream', ...http.authHeaders('required') },
    })
  } catch (cause) {
    if (options.signal?.aborted) {
      throw new ApiError({
        code: CLIENT_ABORTED,
        message: '流已被取消',
        traceId: '',
        retryable: false,
        cause,
      })
    }
    throw new ApiError({
      code: CLIENT_NETWORK,
      message: '流连接失败',
      traceId: '',
      retryable: true,
      cause,
    })
  }

  if (response.ok) return response

  const traceId = response.headers.get(TRACE_ID_HEADER) ?? ''
  const error = await toStreamError(response, traceId)

  if (response.status === 401 && mayRetry && options.onUnauthorized) {
    const refreshed = await options.onUnauthorized(error, tokenUsed)
    if (refreshed) return openStream(options, false)
  }
  throw error
}

async function consume(
  response: Response,
  options: ReadAgentStreamOptions,
): Promise<StreamEndReason> {
  const body = response.body
  if (!body) {
    throw new ApiError({
      code: CLIENT_NETWORK,
      message: '流没有响应体',
      traceId: '',
      retryable: true,
    })
  }

  let sawDone = false
  const parser = createParser({
    onEvent(message) {
      const decoded = decodeSseEvent(message.event ?? '', message.data)
      if (!decoded.ok) {
        // ⚠ 单条坏数据或未知事件名 —— 记一条就走，不炸整条流
        options.onDecodeFailure?.(decoded)
        return
      }
      if (decoded.event.kind === 'done') sawDone = true
      options.onEvent(decoded.event)
    },
  })

  const reader = body.getReader()
  const decoder = new TextDecoder()

  try {
    for (;;) {
      const chunk = await reader.read()
      if (chunk.done) break
      // ⚠ stream: true —— 一个多字节字符可能被切在两个 chunk 之间
      parser.feed(decoder.decode(chunk.value, { stream: true }))
      if (sawDone) break
    }
  } catch (cause) {
    if (options.signal?.aborted) return 'aborted'
    throw new ApiError({
      code: CLIENT_NETWORK,
      message: '流中断',
      traceId: '',
      retryable: true,
      cause,
    })
  } finally {
    // ⚠⚠ **不要 await**：收到 `done` 之后我们主动 break，此时连接可能还开着
    //   (服务端发完 done 不一定立刻关，中间的 keep-alive 代理也会拖着)。
    //   await 在那种情况下会一直挂住，于是「这一轮结束」这件事永远不返回 ——
    //   界面卡在「停止中」，而没有任何报错。发出去就不管，是对的。
    void reader.cancel().catch(() => undefined)
  }

  if (sawDone) return 'done'
  return options.signal?.aborted ? 'aborted' : 'closed'
}

async function toStreamError(response: Response, traceId: string): Promise<ApiError> {
  let code = 'COMMON.INTERNAL'
  let message = `打开流失败(HTTP ${String(response.status)})`
  try {
    const body: unknown = JSON.parse(await response.text())
    if (body && typeof body === 'object' && 'code' in body && typeof body.code === 'string') {
      code = body.code
      if ('message' in body && typeof body.message === 'string') message = body.message
    }
  } catch {
    // 网关 502 之类：响应体不是我们的 JSON，用上面的兜底
  }
  return new ApiError({ code, message, traceId, retryable: false, httpStatus: response.status })
}
